import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@material-ui/core";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { Panel } from "@fluentui/react";
import { getApiUrl } from "../../api-clients";
import {
  ClassScheduleApi,
  ClassScheduleDto,
} from "../../api-clients/api";
import { SchoolContext } from "../../contexts/SchoolContext";
import { AppContext } from "../../contexts/AppContext";
import Loading from "../Shared/Loading";
import CRUDClassSchedule from "../CRUD/CRUDClassSchedule";
import { daysOfWeekOrdered, dayOfWeekToString, timeSpanToString } from "../../helper";
import { useTranslation } from "react-i18next";
import { useRole } from "../../hooks/useRole";

interface IProps {
  classroomId: string;
}
export default function ClassroomSchedule({ classroomId }: IProps) {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const { accademicYear } = useContext(SchoolContext);
  const { openSnackbar } = useContext(AppContext);
  const [classScheduleApi] = useState<ClassScheduleApi>(
    new ClassScheduleApi(getApiUrl())
  );
  const [schedule, setSchedule] = useState<ClassScheduleDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [slotToEdit, setSlotToEdit] = useState<ClassScheduleDto | null>(null);
  const [addingForDay, setAddingForDay] = useState<number | null>(null);

  const loadSchedule = () => {
    if (accademicYear?.accademicYearId == null) return;
    setIsLoading(true);
    classScheduleApi
      .classroom(classroomId, accademicYear.accademicYearId)
      .then((res) => {
        setSchedule(res);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    loadSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classScheduleApi, classroomId, accademicYear]);

  const deleteSlot = (classScheduleId: string) => {
    classScheduleApi
      .delete(classScheduleId)
      .then(() => loadSchedule())
      .catch(() => openSnackbar(t("common.errorOccurred"), "error"));
  };

  return (
    <Card>
      <CardHeader title={t("classSchedule.title")} />
      <CardContent>
        {!isLoading ? (
          <Grid container spacing={1}>
            {daysOfWeekOrdered.map((day) => {
              const daySlots = schedule
                .filter((s) => s.dayOfWeek === day)
                .sort((a, b) =>
                  (a.startTime ?? "").localeCompare(b.startTime ?? "")
                );
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={day}>
                  <Paper variant="outlined" style={{ padding: "8px", minHeight: "160px" }}>
                    <Grid container justify="space-between" alignItems="center">
                      <Typography variant="subtitle2">
                        {dayOfWeekToString(day)}
                      </Typography>
                      {isAdmin && (
                        <IconButton
                          size="small"
                          aria-label={t("classSchedule.addSlot")}
                          onClick={() => setAddingForDay(+day)}
                        >
                          <AddCircleIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Grid>
                    {daySlots.length === 0 ? (
                      <Typography variant="caption" color="textSecondary">
                        {t("shared.noResult")}
                      </Typography>
                    ) : (
                      daySlots.map((s) => (
                        <Paper
                          key={s.classScheduleId}
                          variant="outlined"
                          style={{
                            padding: "4px 8px",
                            marginTop: "4px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <Typography variant="caption" display="block">
                              {timeSpanToString(s.startTime)} -{" "}
                              {timeSpanToString(s.endTime)}
                            </Typography>
                            <Typography variant="body2">
                              {s.subjectName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {s.teacherName}
                            </Typography>
                          </div>
                          {isAdmin && (
                            <div>
                              <IconButton
                                size="small"
                                aria-label={t("common.edit")}
                                onClick={() => setSlotToEdit(s)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label={t("common.delete")}
                                onClick={() =>
                                  s.classScheduleId &&
                                  deleteSlot(s.classScheduleId)
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}
                        </Paper>
                      ))
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Loading />
        )}
      </CardContent>

      <Panel
        isBlocking={true}
        headerText={t("classSchedule.editTitle")}
        isOpen={isAdmin && slotToEdit != null}
        onDismiss={() => setSlotToEdit(null)}
        closeButtonAriaLabel={t("common.close")}
      >
        {slotToEdit != null && (
          <CRUDClassSchedule
            classSchedule={slotToEdit}
            classroomId={classroomId}
            scheduleChanged={() => {
              setSlotToEdit(null);
              loadSchedule();
            }}
          />
        )}
      </Panel>
      <Panel
        isBlocking={true}
        headerText={t("classSchedule.addTitle")}
        isOpen={isAdmin && addingForDay != null}
        onDismiss={() => setAddingForDay(null)}
        closeButtonAriaLabel={t("common.close")}
      >
        {addingForDay != null && (
          <CRUDClassSchedule
            classSchedule={
              { dayOfWeek: addingForDay } as unknown as ClassScheduleDto
            }
            classroomId={classroomId}
            scheduleChanged={() => {
              setAddingForDay(null);
              loadSchedule();
            }}
          />
        )}
      </Panel>
    </Card>
  );
}
